import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { AssetListItem } from '@fleet-bffs/api-contract';
import { resetAssetsSelection } from '~/domain/inventory-list/store/asset-list-reducer';
import type { DashboardMapClusterSelected } from '~/domain/inventory-list/data/AssetListRecord';
import type { InventoryV2FilterParams, InventoryV2MapSeed } from '../models/inventory-v2';

/** A dashboard map cluster handed to `/inventory`, with its assets as the map held them. */
export interface InventoryV2HandOff {
  cluster: DashboardMapClusterSelected;
  seeds: InventoryV2MapSeed[];
}

export interface InventoryV2SelectionState {
  /** When on, every asset matching `filters` is selected except `toggled` and `seeds`. */
  selectAll: boolean;
  /** Filters select all resolves its ids with. */
  filters: InventoryV2FilterParams;
  /** Matching assets when select all was turned on, the count shown until the ids resolve. */
  total: number;
  /** Picked rows as they were loaded, or the rows left out when `selectAll` is on. */
  toggled: AssetListItem[];
  /**
   * Assets toggled on the map, kept as the map holds them because their rows may not be loaded:
   * picked, or left out when `selectAll` is on.
   */
  seeds: InventoryV2MapSeed[];
  /** A dashboard map cluster handed to `/inventory`, waiting for the list to clear its filters. */
  pendingHandOff: InventoryV2HandOff | null;
  /** Router location key of the last cluster handed over, so going back to it does not reapply it. */
  handOffKey: string | null;
  /** When select all was turned on; keys its own range request, so each tick fetches fresh ids. */
  requestedAt: number;
  /**
   * Set when a filter change ends select all, while the ids it selected are checked against the new
   * filters; `0` once they are picked or the selection changes another way.
   */
  pickToken: number;
}

export const initialInventoryV2SelectionState: InventoryV2SelectionState = {
  selectAll: false,
  filters: {},
  total: 0,
  toggled: [],
  seeds: [],
  pendingHandOff: null,
  handOffKey: null,
  requestedAt: 0,
  pickToken: 0,
};

const cleared = (state: InventoryV2SelectionState): InventoryV2SelectionState => ({
  ...initialInventoryV2SelectionState,
  pendingHandOff: state.pendingHandOff,
  handOffKey: state.handOffKey,
});

type ToggleAllPayload = { filters: InventoryV2FilterParams; total: number };

const slice = createSlice({
  name: 'inventoryV2Selection',
  initialState: initialInventoryV2SelectionState,
  reducers: {
    toggleInventoryV2Asset: (state, action: PayloadAction<AssetListItem>) => {
      const id = action.payload.assetDetails.id;
      const seedIndex = state.seeds.findIndex((seed) => seed.iprId === id);
      if (seedIndex !== -1) {
        state.seeds.splice(seedIndex, 1);
        return;
      }
      const index = state.toggled.findIndex((item) => item.assetDetails.id === id);
      if (index === -1) {
        state.toggled.push(action.payload);
      } else {
        state.toggled.splice(index, 1);
      }
    },
    toggleInventoryV2All: {
      reducer: (state, action: PayloadAction<ToggleAllPayload & { requestedAt: number }>) => {
        if (state.selectAll && state.toggled.length === 0 && state.seeds.length === 0) {
          return cleared(state);
        }
        const { filters, total, requestedAt } = action.payload;
        const sameRequest =
          state.selectAll && JSON.stringify(state.filters) === JSON.stringify(filters);
        return {
          ...cleared(state),
          selectAll: true,
          filters,
          total,
          requestedAt: sameRequest ? state.requestedAt : requestedAt,
        };
      },
      prepare: (payload: ToggleAllPayload) => ({
        payload: { ...payload, requestedAt: Date.now() },
      }),
    },
    /**
     * Selects the given map assets, or unselects them when all of them are selected already, as
     * a Cmd/Ctrl-click on the map does.
     */
    toggleInventoryV2MapSeeds: (state, action: PayloadAction<InventoryV2MapSeed[]>) => {
      const ids = new Set(action.payload.map((seed) => seed.iprId));
      const toggledIds = new Set([
        ...state.toggled.map((asset) => asset.assetDetails.id),
        ...state.seeds.map((seed) => seed.iprId),
      ]);
      const isSelected = (id: string) => toggledIds.has(id) !== state.selectAll;
      const allSelected = [...ids].every(isSelected);
      if (allSelected === state.selectAll) {
        state.seeds.push(...action.payload.filter((seed) => !toggledIds.has(seed.iprId)));
        return;
      }
      state.toggled = state.toggled.filter((asset) => !ids.has(asset.assetDetails.id));
      state.seeds = state.seeds.filter((seed) => !ids.has(seed.iprId));
    },
    /** Keeps a dashboard map cluster handed over at router location `key`, once per location. */
    captureInventoryV2HandOff: (
      state,
      action: PayloadAction<InventoryV2HandOff & { key: string }>
    ) => {
      const { key, cluster, seeds } = action.payload;
      if (state.handOffKey === key) {
        return;
      }
      state.pendingHandOff = { cluster, seeds };
      state.handOffKey = key;
    },
    /** Replaces the selection with the pending dashboard map cluster. */
    applyInventoryV2HandOff: (state) => ({
      ...initialInventoryV2SelectionState,
      seeds: state.pendingHandOff?.seeds ?? [],
      handOffKey: state.handOffKey,
    }),
    discardInventoryV2HandOff: (state) => {
      state.pendingHandOff = null;
    },
    /** Unselects the `checked` assets that are not `kept`, as a filter change does for the rows it drops. */
    pruneInventoryV2Selection: (
      state,
      action: PayloadAction<{ checked: string[]; kept: string[] }>
    ) => {
      const checked = new Set(action.payload.checked);
      const kept = new Set(action.payload.kept);
      const dropped = (id: string) => checked.has(id) && !kept.has(id);
      state.toggled = state.toggled.filter((asset) => !dropped(asset.assetDetails.id));
      state.seeds = state.seeds.filter((seed) => !dropped(seed.iprId));
    },
    /** Ends select all on a filter change, until {@link pickInventoryV2Rows} picks what still matches. */
    endInventoryV2SelectAll: {
      reducer: (state, action: PayloadAction<number>) => ({
        ...cleared(state),
        pickToken: action.payload,
      }),
      prepare: () => ({ payload: Date.now() }),
    },
    /**
     * Picks the rows that still match after {@link endInventoryV2SelectAll}, keeping rows picked
     * meanwhile; does nothing once the selection was cleared, replaced or set to select all.
     */
    pickInventoryV2Rows: (
      state,
      action: PayloadAction<{ token: number; rows: AssetListItem[] }>
    ) => {
      if (!state.pickToken || state.pickToken !== action.payload.token) {
        return;
      }
      const picked = new Set([
        ...state.toggled.map((asset) => asset.assetDetails.id),
        ...state.seeds.map((seed) => seed.iprId),
      ]);
      state.toggled.push(
        ...action.payload.rows.filter((asset) => !picked.has(asset.assetDetails.id))
      );
      state.pickToken = 0;
    },
    clearInventoryV2Selection: cleared,
  },
  extraReducers: (builder) => {
    builder.addCase(resetAssetsSelection, cleared);
  },
});

export const {
  toggleInventoryV2Asset,
  toggleInventoryV2All,
  toggleInventoryV2MapSeeds,
  captureInventoryV2HandOff,
  applyInventoryV2HandOff,
  discardInventoryV2HandOff,
  pruneInventoryV2Selection,
  endInventoryV2SelectAll,
  pickInventoryV2Rows,
  clearInventoryV2Selection,
} = slice.actions;

export default slice.reducer;
